/**
 * Карта района: пространственный вид ПРИНЯТОЙ геометрии.
 *
 * Геометрия здесь производная (сопоставление NGIS-2024 посчитано алгоритмом) и
 * принята только для визуализации. Отсюда три правила экрана, которые нельзя
 * ослабить:
 *
 *   1. Контур без принятой геометрии не появляется на карте ни в каком виде —
 *      ни полигоном, ни центроидом, ни маркером. Он живёт в списке ниже, а
 *      список это не слой.
 *   2. Пометка о производности видна всё время, пока видна карта, и берётся из
 *      ответа сервера. Нет пометки — нет карты.
 *   3. Усечение выдачи объявляется числом, а не угадывается по пустым местам.
 *
 * Площади на экране — канонические (микрогектары реестра). Площадь по
 * геометрии NGIS с ними не сходится и здесь не складывается ни с чем.
 *
 * Демо-слой (тематика по культурам из каскада L2) выключен по умолчанию и
 * живёт в собственном параметре адреса. Пока он выключен, экран не запрашивает
 * синтетику вовсе: раскрашивать нечем — сервер не присылает ни культуры, ни
 * гектаров. Пока он включён, пометка «ДЕМО» видна всё время, что видна
 * раскраска, — как и пометка о производности геометрии.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAnalysisContext } from "../../analysis/context";
import {
  api,
  ApiError,
  type ActorDetail,
  type ContourFeatureProps,
  type ContourRow,
  type MapContours,
} from "../../api/client";
import { useRequest } from "../../api/useRequest";
import { useWindowedCollection } from "../../api/useWindowedCollection";
import { layout, PageHeader } from "../../components/AppShell";
import { DetailDrawer } from "../../components/DetailDrawer";
import { Disclosure } from "../../components/Disclosure";
import { districtStep, LevelTrail } from "../../components/LevelTrail";
import { Select, type ChoiceOption } from "../../components/Select";
import { VirtualTable, type Column } from "../../components/VirtualTable";
import {
  Area,
  Badge,
  Button,
  Card,
  Count,
  DataStatePanel,
  InlineAlert,
  MetricCard,
  RequestErrorPanel,
  ScopeNotice,
} from "../../components/ui";
import { countOf, WORDS } from "../../astra/lib/format";
import { COMMON } from "../../i18n/common";
import { defineMessages } from "../../i18n/define";
import { useT } from "../../i18n/hooks";
import { cropLabel } from "../../lib/crops";
import { formatArea, formatCount } from "../../lib/format";
import { DENSITY, levelPath, preview } from "../../lib/levels";
import features from "../features.module.css";
import { MapCanvas } from "./MapCanvas";
import {
  MapPreviewAction,
  MapPreviewCard,
  MapPreviewLevelLinks,
} from "./MapPreviewCard";
import {
  buildCropPalette,
  buildMassifPalette,
  contourLayerData,
  contourListFilters,
  cropCountsInView,
  cropTokenColor,
  foldCropPalette,
  frameRows,
  DISTRICT_FALLBACK_BBOX,
  formatShare,
  geoBounds,
  isSharedRelation,
  massifCountsInView,
  parseThematic,
  referenceLayerData,
  relationLabel,
  share,
  THEMATIC_OPTIONS,
  tierLabel,
  tierRank,
  truncationNotice,
  type MapView,
} from "./mapModel";
import {
  tierEntry,
  TIER_REGISTRY,
  UNKNOWN_TIER,
} from "./tierRegistry";
import styles from "./map.module.css";

const m = defineMessages({
  demoBadge: {
    id: "datamap.demo.badge",
    defaultMessage: "ДЕМО",
    description:
      "Бейдж на плашке карты при раскраске по культурам: данные синтетические; заглавными, одно слово",
  },
  title: {
    id: "datamap.screen.title",
    defaultMessage:
      "Карта района",
    description:
      "Заголовок экрана «Карта данных» и последний шаг хлебных крошек (хоким, менеджер)",
  },
  purposeFill: {
    id: "datamap.screen.purpose",
    defaultMessage:
      "Карта — вид уровня: район целиком либо один массив, при фокусе — земля одного хозяйства. На ней только контуры с ПРИНЯТОЙ геометрией; остальные не рисуются даже приблизительно и перечислены списком внизу. Цвет заливки — {demo, select, yes {СИНТЕТИЧЕСКАЯ культура контура (демо-слой)} other {массив}}, штриховка обводки — ярус принятой связи. Нажатие на контур открывает превью справа, а не уносит с карты; тот же кадр строками и клавиатурой — в таблице под полотном.",
    description:
      "Пояснение под заголовком экрана карты (хоким, менеджер); {demo} — «yes», если включена демо-раскраска по культурам, иначе «no»; ПРИНЯТОЙ и СИНТЕТИЧЕСКАЯ — заглавными намеренно",
  },
  allRegistry: {
    id: "datamap.screen.allRegistry",
    defaultMessage:
      "Весь реестр контуров",
    description:
      "Кнопка в шапке экрана карты: открыть реестр контуров без сужения карты",
  },
  massifAlertTitle: {
    id: "datamap.massifAlert.title",
    defaultMessage:
      "Карта сужена до одного массива",
    description:
      "Заголовок предупреждения над картой: включён фильтр по массиву",
  },
  massifAlertBody: {
    id: "datamap.massifAlert.body",
    defaultMessage:
      "Показаны контуры массива <b>{name}</b>. Остальные контуры района не пропали — они отфильтрованы. Числа покрытия ниже остаются РАЙОННЫМИ: сервер считает их по всему району, и выдавать их за показатели массива было бы подменой знаменателя.",
    description:
      "Текст предупреждения над картой при фильтре по массиву; {name} — имя массива либо «#номер»; <b> — выделение; РАЙОННЫМИ заглавными намеренно",
  },
  massifLevel: {
    id: "datamap.massifAlert.level",
    defaultMessage:
      "Уровень массива",
    description:
      "Ссылка: перейти на экран массива (в предупреждении над картой и в панели «Вид уровня»)",
  },
  resetFilter: {
    id: "datamap.massifAlert.reset",
    defaultMessage:
      "снять фильтр",
    description:
      "Кнопка-ссылка в предупреждении над картой: убрать фильтр по массиву; строчная, коротко",
  },
  truncTitle: {
    id: "datamap.truncation.title",
    defaultMessage:
      "Показано не всё, что попадает в эту область",
    description:
      "Заголовок предупреждения над картой: сервер обрезал выдачу лимитом",
  },
  truncHidden: {
    id: "datamap.truncation.hidden",
    defaultMessage:
      "Скрыто <b>{hidden}</b> {hidden, plural, one {контур} few {контура} many {контуров} other {контура}} из {matching}.",
    description:
      "Продолжение предупреждения об обрезанной выдаче карты: сколько контуров скрыто из скольких; {hidden} и {matching} — числа",
  },
  tilesErrorTitle: {
    id: "datamap.tilesError.title",
    defaultMessage:
      "Контуры этой области не загрузились",
    description:
      "Заголовок ошибки над картой: запрос контуров текущей рамки не удался",
  },
  tilesErrorStale: {
    id: "datamap.tilesError.stale",
    defaultMessage:
      "На карте осталась предыдущая выдача — она относится к другой области.",
    description:
      "Пояснение под ошибкой загрузки контуров: на карте остались старые полигоны",
  },
  statusLoading: {
    id: "datamap.status.loading",
    defaultMessage:
      "загружаем область…",
    description:
      "Плашка на углу карты: идёт загрузка контуров рамки; строчная",
  },
  statusShown: {
    id: "datamap.status.shown",
    defaultMessage:
      "на карте {returned} {returned, plural, one {контур} few {контура} many {контуров} other {контура}} из {matching} в этой области",
    description:
      "Плашка на углу карты: сколько контуров нарисовано из скольких в рамке; {returned} и {matching} — числа; строчная",
  },
  statusIdle: {
    id: "datamap.status.idle",
    defaultMessage:
      "область ещё не запрошена",
    description:
      "Плашка на углу карты: контуры ещё не запрашивались; строчная",
  },
  demoPlate: {
    id: "datamap.demo.plate",
    defaultMessage:
      "синтетическая детализация размещения: культура контура выведена генератором, а не заявлена источником",
    description:
      "Плашка на карте рядом с бейджем ДЕМО при раскраске по культурам: данные синтетические; строчная",
  },
  levelCard: {
    id: "datamap.level.card",
    defaultMessage:
      "Вид уровня",
    description:
      "Заголовок карточки в правой панели карты: выбор массива и ссылки на соседние виды",
  },
  levelSelect: {
    id: "datamap.level.select",
    defaultMessage:
      "Что в кадре",
    description:
      "Подпись выпадающего списка массивов в панели карты",
  },
  levelExplain: {
    id: "datamap.level.explain",
    defaultMessage:
      "Массив уходит фильтром на сервер: сужается и карта, и список контуров под ней. Числа покрытия ниже остаются РАЙОННЫМИ — сервер считает их по всему району.",
    description:
      "Подсказка к выпадающему списку массивов: что делает фильтр; РАЙОННЫМИ заглавными намеренно",
  },
  levelNoRef: {
    id: "datamap.level.noReference",
    defaultMessage:
      "Справочник массивов не загружен: выбирать не из чего.",
    description:
      "Пояснение под списком массивов, пока справочник не загрузился",
  },
  levelWhole: {
    id: "datamap.level.whole",
    defaultMessage:
      "Район целиком",
    description:
      "Первый вариант в списке массивов: без фильтра, весь район",
  },
  levelWholeHint: {
    id: "datamap.level.wholeHint",
    defaultMessage:
      "все контуры среза",
    description:
      "Пояснение к варианту «Район целиком» в списке массивов; строчная",
  },
  inFrame: {
    id: "datamap.legend.inFrame",
    defaultMessage:
      "{count} в кадре",
    description:
      "Подпись справа в строке легенды карты и в списке массивов: сколько контуров этого массива/культуры/яруса видно в текущей рамке; {count} — число; коротко",
  },
  sameList: {
    id: "datamap.level.sameList",
    defaultMessage:
      "Те же контуры списком",
    description:
      "Ссылка в панели карты: открыть реестр контуров с теми же фильтрами",
  },
  carryNote: {
    id: "datamap.level.carryNote",
    defaultMessage:
      "Списком переедет не всё: {items}.",
    description:
      "Примечание под ссылкой «Те же контуры списком»: перечень сужений карты, которые не переносятся в список; {items} — перечень «название — причина» через «;»",
  },
  carryItem: {
    id: "datamap.level.carryItem",
    defaultMessage:
      "{label} — {why}",
    description:
      "Один пункт перечня непереносимых сужений: {label} — название сужения, {why} — причина",
  },
  themeCard: {
    id: "datamap.theme.card",
    defaultMessage:
      "Тематика заливки",
    description:
      "Заголовок карточки в панели карты: чем раскрашены контуры",
  },
  themeSelect: {
    id: "datamap.theme.select",
    defaultMessage:
      "Чем раскрашены контуры",
    description:
      "Подпись выпадающего списка тематик заливки",
  },
  themeExplain: {
    id: "datamap.theme.explain",
    defaultMessage:
      "Заливка отвечает на один вопрос за раз. «Культура» — синтетическая детализация размещения (демо-данные), и пока она выбрана, пометка ДЕМО видна всё время, что видна раскраска.",
    description:
      "Подсказка к списку тематик заливки; ДЕМО заглавными, «Культура» — название варианта в кавычках",
  },
  themeDesc: {
    id: "datamap.theme.description",
    defaultMessage:
      "Недоступные темы оставлены в списке: это граница версии, а не пропуск.",
    description:
      "Пояснение под списком тематик: почему в нём есть недоступные варианты",
  },
  massifsFailed: {
    id: "datamap.theme.massifsFailed",
    defaultMessage:
      "Массивы не загрузились",
    description:
      "Заголовок ошибки в легенде карты: справочник массивов не загрузился",
  },
  massifsFailedNote: {
    id: "datamap.theme.massifsFailedNote",
    defaultMessage:
      "Пока справочник не загружен, контуры залиты нейтральным серым: выдавать чужой цвет за цвет массива нельзя.",
    description:
      "Пояснение под ошибкой справочника массивов в легенде карты",
  },
  frameEmptyLegend: {
    id: "datamap.theme.frameEmpty",
    defaultMessage:
      "В этом кадре нет ни одного контура с принятой геометрией — красить нечего.",
    description:
      "Пояснение в легенде карты: в текущей рамке нет полигонов",
  },
  moreMassifs: {
    id: "datamap.theme.moreMassifs",
    defaultMessage:
      "Ещё {count, plural, one {# массив} few {# массива} many {# массивов} other {# массива}} в кадре",
    description:
      "Заголовок раскрывашки в легенде карты: массивы, не поместившиеся в краткий список; {count} — число",
  },
  showColors: {
    id: "datamap.theme.showColors",
    defaultMessage:
      "показать цвета",
    description:
      "Подпись кнопки раскрывашки в легенде карты: показать остальные цвета массивов; строчная",
  },
  registryMassifs: {
    id: "datamap.theme.registryMassifs",
    defaultMessage:
      "В справочнике района {count, plural, one {# массив} few {# массива} many {# массивов} other {# массива}}; здесь названы только те, что попали в кадр.",
    description:
      "Примечание под легендой массивов на карте; {count} — число массивов района",
  },
  demoInfoTitle: {
    id: "datamap.demoInfo.title",
    defaultMessage:
      "Это выведенные данные, а не заявленные",
    description:
      "Заголовок предупреждения в панели карты при раскраске по культурам (демо)",
  },
  demoInfoBody: {
    id: "datamap.demoInfo.body",
    defaultMessage:
      "Раскраска показывает культуру, стоящую на контуре НА ДАТУ СРЕЗА, а не за весь сезон: за год гектар несёт хлопок, потом озимые, потом второстепенную культуру. Источник не говорит ни где лежат эти гектары, ни когда — раскладку и сроки посчитал генератор из реального размещения, землепользования и нормативных окон культур. Сходиться с реестром она обязана только по суммам строк.",
    description:
      "Текст предупреждения в панели карты при демо-раскраске по культурам; НА ДАТУ СРЕЗА заглавными намеренно",
  },
  otherCrops: {
    id: "datamap.demo.otherCrops",
    defaultMessage:
      "Прочие культуры",
    description:
      "Строка легенды карты (демо): нейтральный серый для прочих культур",
  },
  otherCropsFolded: {
    id: "datamap.demo.otherCropsFolded",
    defaultMessage:
      "Прочие культуры · свёрнуто культур: {count} — числа в таблице",
    description:
      "Строка легенды карты (демо): прочие культуры, в том числе свёрнутые из-за лимита цветов; {count} — число свёрнутых культур",
  },
  nothingSown: {
    id: "datamap.demo.nothingSown",
    defaultMessage:
      "Сейчас ничего не стоит",
    description:
      "Строка легенды карты (демо): контуры, на которых на дату среза нет культуры",
  },
  covTableTitle: {
    id: "datamap.demo.coverageTitle",
    defaultMessage:
      "Покрытие детализацией по району",
    description:
      "Заголовок раскрывашки в панели карты (демо): таблица покрытия синтетической детализацией",
  },
  covTableMore: {
    id: "datamap.demo.coverageMore",
    defaultMessage:
      "показать таблицу",
    description:
      "Подпись кнопки раскрывашки «Покрытие детализацией по району»; строчная",
  },
  covCaption: {
    id: "datamap.demo.coverageCaption",
    defaultMessage:
      "Покрытие детализацией по району. Гектары синтетические и с заявленным посевом не складываются.",
    description:
      "Подпись таблицы покрытия синтетической детализацией (читают озвучиватели и под таблицей)",
  },
  covCrop: {
    id: "datamap.demo.colCrop",
    defaultMessage:
      "Культура",
    description:
      "Заголовок колонки таблицы покрытия детализацией: культура",
  },
  covDemoHa: {
    id: "datamap.demo.colDemoHa",
    defaultMessage:
      "Демо, га",
    description:
      "Заголовок колонки таблицы покрытия детализацией: синтетические гектары; узкая колонка",
  },
  covContours: {
    id: "datamap.demo.colContours",
    defaultMessage:
      "Контуров",
    description:
      "Заголовок колонки таблицы покрытия детализацией: число контуров; узкая колонка",
  },
  covOnMap: {
    id: "datamap.demo.colOnMap",
    defaultMessage:
      "Из них на карте",
    description:
      "Заголовок колонки таблицы покрытия детализацией: сколько из контуров нарисовано на карте",
  },
  covTotal: {
    id: "datamap.demo.rowTotal",
    defaultMessage:
      "Всего (демо)",
    description:
      "Строка итога таблицы покрытия детализацией: сумма синтетических данных",
  },
  covSource: {
    id: "datamap.demo.rowSource",
    defaultMessage:
      "Заявлено источником (факт)",
    description:
      "Строка таблицы покрытия детализацией: фактические гектары посева по данным источника",
  },
  covPlacements: {
    id: "datamap.demo.placements",
    defaultMessage:
      "строк размещения: {count}",
    description:
      "Ячейка таблицы покрытия детализацией: сколько строк размещения заявил источник; {count} — число",
  },
  batchNote: {
    id: "datamap.demo.batch",
    defaultMessage:
      "Партия #{id}: {generator}, нормативы {norms}, seed {seed}.",
    description:
      "Служебная пометка под таблицей демо-детализации: какая партия синтетики загружена; {id} — номер, {generator} и {norms} — версии генератора и нормативов, {seed} — зерно генерации; технические значения не переводить",
  },
  batchUnallocated: {
    id: "datamap.demo.batchUnallocated",
    defaultMessage:
      "Не разложено мер: {count} ({area} га) — у этих хозяйств не нашлось подходящей земли.",
    description:
      "Продолжение служебной пометки о партии синтетики: сколько мер не удалось разложить по земле; {count} — число, {area} — гектары",
  },
  demoMissingTitle: {
    id: "datamap.demo.missingTitle",
    defaultMessage:
      "Демо-слой не собран",
    description:
      "Заголовок пустого состояния в панели карты: синтетической детализации нет в базе",
  },
  demoMissingBody: {
    id: "datamap.demo.missingBody",
    defaultMessage:
      "Синтетической детализации в базе нет. Её собирает администратор служебной командой; до этого раскрашивать карту по культурам нечем.",
    description:
      "Пояснение пустого состояния в панели карты: демо-данных нет в базе",
  },
  tierCard: {
    id: "datamap.tiers.card",
    defaultMessage:
      "Ярус принятой связи",
    description:
      "Заголовок карточки в панели карты: легенда ярусов связи «фича NGIS ↔ контур»",
  },
  tierName: {
    id: "datamap.tiers.name",
    defaultMessage:
      "{tier} — {relation}",
    description:
      "Строка легенды ярусов: {tier} — подпись яруса («групповой»), {relation} — отношение («фича = часть контура»)",
  },
  tierCounts: {
    id: "datamap.tiers.counts",
    defaultMessage:
      "{contours} · {features}",
    description:
      "Счёт справа в строке легенды ярусов: {contours} — «5 контуров», {features} — «7 фич»",
  },
  feature: {
    id: "datamap.tiers.feature",
    defaultMessage:
      "{count, plural, one {# фича} few {# фичи} many {# фич} other {# фичи}}",
    description:
      "Число фич NGIS (отдельных полигонов исходной геометрии) с числом #",
  },
  tierNote: {
    id: "datamap.tiers.note",
    defaultMessage:
      "Ярус — это то, как связь «фича NGIS ↔ контур» была принята, а отношение рядом с ним — что связь утверждает о геометрии. Штриховка образца повторяет обводку контура на карте.",
    description:
      "Пояснение под легендой ярусов в панели карты; NGIS — источник геометрии, не переводить",
  },
  tierShared: {
    id: "datamap.tiers.shared",
    defaultMessage:
      "Ярусы «{names}» устроены наоборот остальных: там ОДНА фича показывает НЕСКОЛЬКО контуров, поэтому у соседних контуров виден один и тот же полигон. Это не ошибка данных и не «часть контура»: гектары такой фичи делятся между её контурами и входят в районный итог один раз.",
    description:
      "Пояснение под легендой ярусов: чем особенны объединяющие ярусы; {names} — их подписи через запятую; ОДНА и НЕСКОЛЬКО заглавными намеренно",
  },
  ctxCard: {
    id: "datamap.context.card",
    defaultMessage:
      "Контекстные слои",
    description:
      "Заголовок карточки в панели карты: подложки-ориентиры",
  },
  ctxDistrict: {
    id: "datamap.context.district",
    defaultMessage:
      "Граница района",
    description:
      "Строка легенды контекстных слоёв карты: контур района",
  },
  ctxMahalla: {
    id: "datamap.context.mahalla",
    defaultMessage:
      "Махалли",
    description:
      "Строка легенды контекстных слоёв карты: границы махалли (сельских сходов граждан)",
  },
  ctxNote: {
    id: "datamap.context.note",
    defaultMessage:
      "Контекстные слои даны для ориентира и не участвуют ни в одном итоге экрана.",
    description:
      "Пояснение под легендой контекстных слоёв карты",
  },
  frameCard: {
    id: "datamap.frame.card",
    defaultMessage:
      "Контуры в кадре ({count})",
    description:
      "Заголовок карточки с таблицей контуров под картой; {count} — сколько контуров в текущей рамке",
  },
  frameEmptyTitle: {
    id: "datamap.frame.emptyTitle",
    defaultMessage:
      "В этом кадре контуров нет",
    description:
      "Заголовок пустого состояния таблицы контуров в кадре",
  },
  frameEmptyBody: {
    id: "datamap.frame.emptyBody",
    defaultMessage:
      "Ни одного контура с принятой геометрией в текущей рамке. Это про рамку, а не про район: сдвиньте кадр или снимите фильтр массива.",
    description:
      "Пояснение пустого состояния таблицы контуров в кадре",
  },
  frameIntro: {
    id: "datamap.frame.intro",
    defaultMessage:
      "Тот же кадр, что на полотне, — строками. Наведение и фокус строки подсвечивают контур на карте; нажатие открывает превью, а не уносит со страницы.",
    description:
      "Пояснение над таблицей контуров в кадре",
  },
  frameTrunc: {
    id: "datamap.frame.truncated",
    defaultMessage:
      "Кадр обрезан лимитом выдачи: строк здесь столько же, сколько полигонов.",
    description:
      "Дописка к пояснению над таблицей контуров, когда выдача карты обрезана лимитом",
  },
  frameHeadContour: {
    id: "datamap.frame.colContour",
    defaultMessage:
      "Контур",
    description:
      "Заголовок колонки таблицы контуров в кадре: номер контура",
  },
  frameHeadMassif: {
    id: "datamap.frame.colMassif",
    defaultMessage:
      "Массив",
    description:
      "Заголовок колонки таблицы контуров в кадре: массив",
  },
  frameHeadUser: {
    id: "datamap.frame.colUser",
    defaultMessage:
      "Землепользователь",
    description:
      "Заголовок колонки таблицы контуров в кадре: хозяйство, за которым записан контур",
  },
  frameHeadArea: {
    id: "datamap.frame.colArea",
    defaultMessage:
      "Площадь, га",
    description:
      "Заголовок колонки таблицы контуров в кадре: площадь в гектарах; узкая колонка",
  },
  moreContours: {
    id: "datamap.frame.moreContours",
    defaultMessage:
      "Ещё {count, plural, one {# контур} few {# контура} many {# контуров} other {# контура}} в кадре",
    description:
      "Заголовок раскрывашки под таблицей контуров в кадре; {count} — число скрытых строк",
  },
  showRows: {
    id: "datamap.frame.showRows",
    defaultMessage:
      "показать строки",
    description:
      "Подпись кнопки раскрывашки под таблицей контуров в кадре; строчная",
  },
  a11yDouble: {
    id: "datamap.a11y.mapDouble",
    defaultMessage:
      "Карта — визуальный дубль. Всё, что на ней показано, доступно строками в таблице «Контуры в кадре» выше и в реестре контуров; контуры без принятой геометрии перечислены отдельным списком на этой же странице.",
    description:
      "Скрытый текст для озвучивателей (только программы чтения экрана): карта дублируется таблицей; «Контуры в кадре» — заголовок таблицы выше",
  },
  kpiWith: {
    id: "datamap.kpi.withGeometry",
    defaultMessage:
      "Контуров с геометрией",
    description:
      "Подпись KPI-плитки под картой: сколько контуров имеют принятую геометрию",
  },
  kpiWithBottom: {
    id: "datamap.kpi.withGeometryBottom",
    defaultMessage:
      "из {total} — {share} реестра",
    description:
      "Мелкая строка под KPI «Контуров с геометрией»; {total} — всего контуров в реестре, {share} — доля с процентом («12,5 %»)",
  },
  kpiHa: {
    id: "datamap.kpi.ha",
    defaultMessage:
      "Гектаров с геометрией",
    description:
      "Подпись KPI-плитки под картой: сколько гектаров лежит в контурах с геометрией",
  },
  kpiHaBottom: {
    id: "datamap.kpi.haBottom",
    defaultMessage:
      "{share} канонической площади района",
    description:
      "Мелкая строка под KPI «Гектаров с геометрией»; {share} — доля с процентом («12,5 %»)",
  },
  kpiWithout: {
    id: "datamap.kpi.without",
    defaultMessage:
      "Контуров без геометрии",
    description:
      "Подпись KPI-плитки под картой: сколько контуров не имеют принятой геометрии",
  },
  kpiWithoutBottom: {
    id: "datamap.kpi.withoutBottom",
    defaultMessage:
      "на карте не показаны вовсе: {area}",
    description:
      "Мелкая строка под KPI «Контуров без геометрии»; {area} — площадь этих контуров с единицей («120 га»)",
  },
  covCard: {
    id: "datamap.coverage.card",
    defaultMessage:
      "Покрытие карты",
    description:
      "Заголовок карточки с таблицей покрытия карты геометрией",
  },
  covCap: {
    id: "datamap.coverage.caption",
    defaultMessage:
      "Знаменатель — канонический реестр целиком. Гектары считаются по канону, а не по геометрии NGIS: её площади с реестром не сходятся, и складывать их нельзя.",
    description:
      "Подпись таблицы покрытия карты; NGIS — источник геометрии, не переводить",
  },
  covWhat: {
    id: "datamap.coverage.colWhat",
    defaultMessage:
      "Что считаем",
    description:
      "Заголовок первой колонки таблицы покрытия карты",
  },
  covColContours: {
    id: "datamap.coverage.colContours",
    defaultMessage:
      "Контуров",
    description:
      "Заголовок колонки таблицы покрытия карты: число контуров",
  },
  covColHa: {
    id: "datamap.coverage.colHa",
    defaultMessage:
      "Гектаров",
    description:
      "Заголовок колонки таблицы покрытия карты: гектары",
  },
  covColShare: {
    id: "datamap.coverage.colShare",
    defaultMessage:
      "Доля площади",
    description:
      "Заголовок колонки таблицы покрытия карты: доля от площади реестра",
  },
  covRowWith: {
    id: "datamap.coverage.rowWith",
    defaultMessage:
      "С принятой геометрией — видно на карте",
    description:
      "Строка таблицы покрытия карты: контуры, которые нарисованы",
  },
  covRowWithout: {
    id: "datamap.coverage.rowWithout",
    defaultMessage:
      "Без геометрии — только списком",
    description:
      "Строка таблицы покрытия карты: контуры, которых на карте нет",
  },
  covRowAll: {
    id: "datamap.coverage.rowAll",
    defaultMessage:
      "Реестр целиком",
    description:
      "Строка итога таблицы покрытия карты",
  },
  partsCaption: {
    id: "datamap.coverage.partsCaption",
    defaultMessage:
      "Части в геометрии и части в реестре — РАЗНЫЕ величины: первая про то, из скольких фич собран полигон, вторая про членение землепользования. Складывать и сравнивать их нельзя.",
    description:
      "Подпись второй таблицы покрытия карты; РАЗНЫЕ заглавными намеренно",
  },
  partsRowPart: {
    id: "datamap.coverage.partsRowPart",
    defaultMessage:
      "Фич NGIS, принятых как часть контура ({relation})",
    description:
      "Строка второй таблицы покрытия: сколько фич NGIS приняты как части контуров; {relation} — подпись отношения («фича = часть контура»)",
  },
  partsPartNote: {
    id: "datamap.coverage.partsPartNote",
    defaultMessage:
      "собирают {contours} — это геометрия",
    description:
      "Пояснение справа в строке «Фич NGIS, принятых как часть контура»; {contours} — число контуров со словом («5 контуров»)",
  },
  partsRowShared: {
    id: "datamap.coverage.partsRowShared",
    defaultMessage:
      "Фич NGIS, показывающих несколько контуров сразу",
    description:
      "Строка второй таблицы покрытия: фичи, у которых один полигон на несколько контуров",
  },
  partsSharedNote: {
    id: "datamap.coverage.partsSharedNote",
    defaultMessage:
      "показывают {contours} — один полигон виден у каждого из них, а его гектары поделены между ними",
    description:
      "Пояснение справа в строке про фичи, показывающие несколько контуров; {contours} — число контуров со словом",
  },
  partsRowCanon: {
    id: "datamap.coverage.partsRowCanon",
    defaultMessage:
      "Частей контуров в каноне",
    description:
      "Строка второй таблицы покрытия: число частей контуров по реестру",
  },
  partsCanonNote: {
    id: "datamap.coverage.partsCanonNote",
    defaultMessage:
      "членение землепользования в реестре — это не геометрия",
    description:
      "Пояснение справа в строке «Частей контуров в каноне»",
  },
  partsRowNoArea: {
    id: "datamap.coverage.partsRowNoArea",
    defaultMessage:
      "Контуров без единой строки площади",
    description:
      "Строка второй таблицы покрытия: контуры, у которых в источнике нет площади",
  },
  partsNoAreaNote: {
    id: "datamap.coverage.partsNoAreaNote",
    defaultMessage:
      "у них нет значения площади в источнике — это не ноль гектаров",
    description:
      "Пояснение справа в строке про контуры без площади",
  },
  noGeoCard: {
    id: "datamap.noGeometry.card",
    defaultMessage:
      "Контуры без принятой геометрии",
    description:
      "Заголовок карточки и подпись таблицы под картой: контуры, которых на карте нет",
  },
  noGeoError: {
    id: "datamap.noGeometry.error",
    defaultMessage:
      "Список не загрузился",
    description:
      "Заголовок ошибки загрузки списка контуров без геометрии",
  },
  noGeoEmpty: {
    id: "datamap.noGeometry.empty",
    defaultMessage:
      "Все контуры среза имеют принятую геометрию.",
    description:
      "Пустое состояние таблицы контуров без геометрии",
  },
  noGeoFound: {
    id: "datamap.noGeometry.found",
    defaultMessage:
      "Найдено <b>{total}</b> {total, plural, one {контур} few {контура} many {контуров} other {контура}}",
    description:
      "Строка над таблицей контуров без геометрии; {total} — сколько найдено",
  },
  noGeoFoundLoading: {
    id: "datamap.noGeometry.foundLoading",
    defaultMessage:
      "Найдено <b>…</b>",
    description:
      "Строка над таблицей контуров без геометрии, пока число неизвестно",
  },
  noGeoUpdating: {
    id: "datamap.noGeometry.updating",
    defaultMessage:
      "обновляем",
    description:
      "Приписка к строке над таблицей контуров без геометрии, идёт обновление; строчная, ставится после « · »",
  },
  noGeoNote: {
    id: "datamap.noGeometry.note",
    defaultMessage:
      "Это список, а не слой: показать их на карте нечем, и рисовать «примерно» мы не будем.",
    description:
      "Пояснение над таблицей контуров без геометрии",
  },
  noGeoLoaded: {
    id: "datamap.noGeometry.loaded",
    defaultMessage:
      "Загружено строк: {loaded}. Строка целиком ведёт на карточку контура.",
    description:
      "Сноска под таблицей контуров без геометрии, когда общее число неизвестно; {loaded} — число",
  },
  noGeoLoadedOf: {
    id: "datamap.noGeometry.loadedOf",
    defaultMessage:
      "Загружено строк: {loaded} из {total}. Строка целиком ведёт на карточку контура.",
    description:
      "Сноска под таблицей контуров без геометрии; {loaded} из {total} — числа",
  },
  colNo: {
    id: "datamap.noGeometry.colNo",
    defaultMessage:
      "Номер",
    description:
      "Заголовок колонки таблицы контуров без геометрии: номер контура; узкая колонка",
  },
  colOpen: {
    id: "datamap.noGeometry.openHint",
    defaultMessage:
      "Открыть карточку контура {no}",
    description:
      "Подсказка при наведении на номер контура в таблице; {no} — номер контура",
  },
  colMassif: {
    id: "datamap.noGeometry.colMassif",
    defaultMessage:
      "Массив",
    description:
      "Заголовок колонки таблицы контуров без геометрии: массив",
  },
  colUser: {
    id: "datamap.noGeometry.colUser",
    defaultMessage:
      "Землепользователь",
    description:
      "Заголовок колонки таблицы контуров без геометрии: хозяйство",
  },
  colNotSet: {
    id: "datamap.noGeometry.notSet",
    defaultMessage:
      "не задан",
    description:
      "Бейдж в колонке «Землепользователь»: у контура не указан землепользователь; строчная, бейдж",
  },
  colSubject: {
    id: "datamap.noGeometry.subject",
    defaultMessage:
      "субъект #{id}",
    description:
      "Значение в колонке «Землепользователь», когда имя неизвестно; {id} — номер субъекта",
  },
  colTotal: {
    id: "datamap.noGeometry.colTotal",
    defaultMessage:
      "Всего, га",
    description:
      "Заголовок колонки таблицы контуров без геометрии: общая площадь в гектарах; узкая колонка",
  },
  previewTitle: {
    id: "datamap.preview.title",
    defaultMessage:
      "Контур {no}",
    description:
      "Заголовок боковой панели превью контура; {no} — номер контура",
  },
  loadingLayers: {
    id: "datamap.state.loading",
    defaultMessage:
      "Загружаем слои карты",
    description:
      "Заголовок экрана, пока грузятся слои карты",
  },
  blocked: {
    id: "datamap.state.blocked",
    defaultMessage:
      "Карта не показывается без пометки о происхождении",
    description:
      "Заголовок блокировки: сервер не прислал пометку о происхождении геометрии, карта не рисуется",
  },
  toRegistry: {
    id: "datamap.state.toRegistry",
    defaultMessage:
      "К реестру контуров",
    description:
      "Кнопка на экране блокировки карты: перейти в реестр контуров",
  },
  rowNoUser: {
    id: "datamap.row.noUser",
    defaultMessage:
      "землепользователь не задан",
    description:
      "Бейдж и часть имени строки в таблице контуров в кадре: у контура нет землепользователя; строчная",
  },
  rowMassifUnnamed: {
    id: "datamap.row.massifUnnamed",
    defaultMessage:
      "массив #{id}",
    description:
      "Имя массива в строке таблицы контуров в кадре, когда справочник не загружен; {id} — номер массива",
  },
  rowAreaMissing: {
    id: "datamap.row.areaMissing",
    defaultMessage:
      "площадь не заявлена",
    description:
      "Часть озвучиваемого имени строки таблицы контуров: у контура нет площади; строчная",
  },
  rowHa: {
    id: "datamap.row.haValue",
    defaultMessage:
      "{value} га",
    description:
      "Площадь с единицей в озвучиваемом имени строки таблицы контуров; {value} — число",
  },
  rowLabel: {
    id: "datamap.row.label",
    defaultMessage:
      "Контур {no}, {massif}, {user}, {area}",
    description:
      "Озвучиваемое имя строки-кнопки в таблице контуров в кадре (программы чтения экрана): номер, массив, землепользователь, площадь через запятую",
  },
  focusName: {
    id: "datamap.focus.unnamed",
    defaultMessage:
      "хозяйство #{id}",
    description:
      "Имя хозяйства фокуса, пока оно не загрузилось; {id} — номер хозяйства; строчная",
  },
  focusLoading: {
    id: "datamap.focus.loading",
    defaultMessage:
      "Загружаем хозяйство фокуса…",
    description:
      "Строка в панели карты: идёт загрузка хозяйства, выбранного для подсветки",
  },
  focusErrorTitle: {
    id: "datamap.focus.errorTitle",
    defaultMessage:
      "Хозяйство фокуса не загрузилось",
    description:
      "Заголовок ошибки в панели карты: хозяйство для подсветки не загрузилось",
  },
  focusErrorBody: {
    id: "datamap.focus.errorBody",
    defaultMessage:
      "Подсветить его контуры нечем, и карта показана без фокуса.",
    description:
      "Текст ошибки загрузки хозяйства фокуса; после него идёт серверное сообщение",
  },
  focusReset: {
    id: "datamap.focus.reset",
    defaultMessage:
      "снять фокус",
    description:
      "Кнопка-ссылка в панели карты: убрать подсветку хозяйства; строчная, коротко",
  },
  focusTitle: {
    id: "datamap.focus.title",
    defaultMessage:
      "Фокус: {name}",
    description:
      "Заголовок плашки фокуса на хозяйстве в панели карты; {name} — название хозяйства",
  },
  focusBody: {
    id: "datamap.focus.body",
    defaultMessage:
      "Подсвечены контуры хозяйства; остальные приглушены, но остались на карте — выборка не сужена. Землепользований у хозяйства <b>{uses}</b>, различных контуров среди них — <b>{contours}</b>.",
    description:
      "Текст плашки фокуса на хозяйстве; {uses} и {contours} — числа; <b> — выделение",
  },
  focusNoGeo: {
    id: "datamap.focus.noGeometry",
    defaultMessage:
      "Контуры без принятой геометрии на карте не появятся вовсе — они в списке ниже.",
    description:
      "Дописка к плашке фокуса: у хозяйства есть контуры без геометрии",
  },
  focusLevel: {
    id: "datamap.focus.level",
    defaultMessage:
      "Уровень хозяйства",
    description:
      "Ссылка в плашке фокуса: перейти на экран хозяйства",
  },
});

/** Код «прочих культур» источника: та же нейтраль, что и свёрнутый хвост. */
const OTHER_CROPS = "OTHER_CROPS";

/** Контекстные слои не перезапрашиваются при движении — упрощаем под «весь
    район целиком», а не под текущий масштаб. */
const REFERENCE_ZOOM = 11;
const PAGE_SIZE = 200;

/**
 * Тематика заливки. Параметр ЭКРАННЫЙ и не едет по ссылкам на другие экраны
 * (`linkSearch` его не переносит): выбранная на карте раскраска — не
 * утверждение о контексте анализа всего приложения.
 */
const THEMATIC_PARAM = "thematic";
/**
 * Прежний адрес демо-слоя. Ссылки на него уже разосланы (обзор, гид, чужие
 * закладки), поэтому он читается и переводится в тематику, но больше не
 * пишется: два имени одного состояния разошлись бы на первой же правке.
 */
const LEGACY_DEMO_PARAM = "demo_layers";
/**
 * Фильтр по массиву. Приезжает с уровня массива и остаётся ЛОКАЛЬНЫМ: в
 * `linkSearch` он не попадает, потому что «массив №7» на чужом экране означал
 * бы другую выборку. Пока он включён, экран обязан говорить это вслух —
 * молчаливо суженная карта читается как «в районе больше ничего нет».
 */
const MASSIF_PARAM = "massif_id";
/**
 * Фокус на хозяйстве. Серверным фильтром он НЕ становится: `/map/contours`
 * землепользователя не принимает и в свойствах фичи его идентификатора не
 * несёт. Поэтому фокус здесь — клиентская подсветка по списку контуров
 * хозяйства, и экран говорит это прямо, а не выдаёт подсветку за выборку.
 */
const ACTOR_PARAM = "actor_id";

export function MapScreen() {
  const t = useT();
  const context = useAnalysisContext();

  const [search, setSearch] = useSearchParams();
  const thematic = parseThematic(
    search.get(THEMATIC_PARAM) ??
      (search.get(LEGACY_DEMO_PARAM) === "true" ? "crop_demo" : null),
  );
  const demoLayers = thematic === "crop_demo";
  const massifFilter = Number(search.get(MASSIF_PARAM)) || null;
  const actorFocus = Number(search.get(ACTOR_PARAM)) || null;

  /** Один способ править адрес экрана: значение либо стоит, либо его нет. */
  const patch = useCallback(
    (key: string, value: string | null) => {
      setSearch(
        (previous) => {
          const next = new URLSearchParams(previous);
          // Умолчание не пишется в адрес вовсе: «по умолчанию» должно
          // читаться из чистой ссылки, а не из `=false`.
          if (value === null || value === "") next.delete(key);
          else next.set(key, value);
          // Старое имя тематики уходит при любой правке: два имени одного
          // состояния расходятся молча.
          next.delete(LEGACY_DEMO_PARAM);
          return next;
        },
        { replace: true },
      );
    },
    [setSearch],
  );

  /**
   * Параметры запросов карты. Синтетика запрашивается ТОЛЬКО при включённом
   * слое: пока он выключен, `crop_demo` каждой фичи приходит пустым, и
   * раскрасить карту по культурам физически нечем.
   */
  const mapParams = useMemo(
    () => ({
      ...context.params,
      ...(demoLayers ? { include_demo: true } : {}),
      ...(massifFilter ? { massif_id: massifFilter } : {}),
      // Фокус на хозяйстве в запрос НЕ уходит: такого фильтра у карты нет.
    }),
    [context.params, demoLayers, massifFilter],
  );
  const mapKey = `${context.key}|${demoLayers}|${massifFilter ?? ""}`;

  const reference = useRequest(
    () => api.mapReference({ ...context.params, zoom: REFERENCE_ZOOM }),
    [context.key],
  );
  const coverage = useRequest(() => api.mapCoverage(context.params), [context.key]);
  const massifs = useRequest(() => api.massifs(context.params), [context.key]);
  const placementCoverage = useRequest(
    // Покрытие размещения фильтра по массиву не принимает: оно остаётся
    // районным, и подписано оно тоже как районное.
    () =>
      api.mapPlacementCoverage(
        demoLayers ? { ...context.params, include_demo: true } : context.params,
      ),
    [`${context.key}|${demoLayers}`],
  );

  /**
   * Хозяйство фокуса. Берётся уровнем хозяйства целиком, потому что нужен его
   * список землепользований: в свойствах фичи карты хозяйства нет вовсе, и
   * подсветить его контуры больше нечем.
   */
  const actor = useRequest<ActorDetail | null>(
    () =>
      actorFocus === null
        ? Promise.resolve(null)
        : api.actor(actorFocus, context.params),
    [context.key, actorFocus],
  );
  const focusIds = useMemo(() => {
    if (!actor.data) return [];
    return [...new Set(actor.data.land_uses.map((use) => use.contour_id))];
  }, [actor.data]);

  const [view, setView] = useState<MapView | null>(null);
  const [tiles, setTiles] = useState<MapContours | null>(null);
  const [tilesError, setTilesError] = useState<ApiError | null>(null);
  const [tilesLoading, setTilesLoading] = useState(false);
  const [retry, setRetry] = useState(0);

  const bbox = view?.bbox ?? null;
  const zoom = view?.zoom ?? null;

  useEffect(() => {
    if (bbox === null || zoom === null) return;
    const controller = new AbortController();
    let active = true;
    setTilesLoading(true);

    api
      // `mapParams` в зависимостях не нужен: он меняется только вместе с
      // `mapKey`, который в списке есть.
      .mapContours({ ...mapParams, bbox, zoom }, controller.signal)
      .then((response) => {
        if (!active) return;
        setTiles(response);
        setTilesError(null);
      })
      .catch((cause: unknown) => {
        if (!active) return;
        // Отмена — решение клиента, а не отказ сервера: пользователь просто
        // продолжил двигать карту. Показывать ему ошибку тут не за что.
        if (cause instanceof ApiError && cause.code === "aborted") return;
        setTilesError(
          cause instanceof ApiError
            ? cause
            : new ApiError("unknown_error", String(cause), 0),
        );
      })
      .finally(() => {
        if (active) setTilesLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [mapKey, bbox, zoom, retry]);

  const palette = useMemo(
    () => buildMassifPalette(massifs.data?.items ?? []),
    [massifs.data],
  );
  const cropPalette = useMemo(() => buildCropPalette(), []);
  const cropsInView = useMemo(
    () => cropCountsInView(tiles?.features ?? []),
    [tiles],
  );
  /**
   * Потолок заливок кадра (ADR-0009) — про плотность легенды, а не про цвет:
   * различимость проверена на КАЖДОЙ паре ступеней, поэтому безопасна любая
   * четвёрка кадра. Хвост не исчезает: он сворачивается в «прочие» и
   * называется в легенде.
   */
  const cropFill = useMemo(
    () => foldCropPalette(cropPalette, cropsInView.byCrop),
    [cropPalette, cropsInView],
  );
  /**
   * Свёрнутые культуры продолжают краситься — нейтралью «прочие», а не
   * запасным цветом слоя: «прочие» и «данных нет» — разные утверждения.
   */
  const cropFillPalette = useMemo(() => {
    if (cropFill.folded.length === 0) return cropFill.shown;
    const other = cropTokenColor("--data-crop-other");
    const foldedEntries = cropPalette
      .filter((entry) => cropFill.folded.includes(entry.code))
      .map((entry) => ({ ...entry, color: other, token: "--data-crop-other" }));
    return [...cropFill.shown, ...foldedEntries];
  }, [cropFill, cropPalette]);
  /**
   * Легенда серого: «прочие культуры» источника и свёрнутый хвост красятся
   * ОДНОЙ нейтралью, значит и строка у них одна. Две строки под один образец
   * читались бы как два разных цвета, которых на карте нет.
   */
  const cropOther = useMemo(() => {
    const shownOther = cropFill.shown.find((entry) => entry.code === OTHER_CROPS);
    const folded = cropFill.folded;
    if (!shownOther && folded.length === 0) return null;
    const count =
      (shownOther ? (cropsInView.byCrop.get(OTHER_CROPS) ?? 0) : 0) +
      folded.reduce((sum, code) => sum + (cropsInView.byCrop.get(code) ?? 0), 0);
    return { count, folded };
  }, [cropFill, cropsInView]);
  const massifNames = useMemo(() => {
    const index = new Map<number, string>();
    for (const entry of palette) index.set(entry.id, entry.name);
    return index;
  }, [palette]);
  const massifName = useCallback(
    (massifId: number) => massifNames.get(massifId) ?? null,
    [massifNames],
  );

  const district = useMemo(
    () => referenceLayerData(reference.data?.district.features ?? []),
    [reference.data],
  );
  const mahallas = useMemo(
    () => referenceLayerData(reference.data?.mahallas.features ?? []),
    [reference.data],
  );
  const contours = useMemo(
    () => contourLayerData(tiles?.features ?? []),
    [tiles],
  );
  const fitBbox = useMemo(
    () =>
      geoBounds(reference.data?.district.features ?? []) ??
      DISTRICT_FALLBACK_BBOX,
    [reference.data],
  );

  const fetchWithoutGeometry = useCallback(
    (offset: number, limit: number) =>
      api.contours({ ...context.params, has_geometry: false, limit, offset }),
    [context.params],
  );
  const withoutGeometry = useWindowedCollection<ContourRow>(
    fetchWithoutGeometry,
    `${context.key}|no-geometry`,
    PAGE_SIZE,
  );

  /**
   * Легенда массивов считается по ТОМУ ЖЕ ответу, который раскрашен: она
   * называет цвета, которые в кадре есть, а не весь справочник района. Прежний
   * свёрнутый список из 22 массивов не отвечал на вопрос, ради которого в
   * легенду и смотрят, — «что это за цвет вот здесь».
   */
  const massifsInView = useMemo(
    () => massifCountsInView(tiles?.features ?? []),
    [tiles],
  );
  const massifLegend = useMemo(() => {
    const rows = palette
      .map((entry) => ({
        ...entry,
        inView: massifsInView.byMassif.get(entry.id) ?? 0,
      }))
      .filter((entry) => entry.inView > 0)
      .sort(
        (left, right) =>
          right.inView - left.inView || left.name.localeCompare(right.name, "ru"),
      );
    return preview(rows, DENSITY.preview);
  }, [palette, massifsInView]);

  /** Таблица-компаньон: та же выдача кадра, доступная клавиатурой. */
  const rowsInFrame = useMemo(() => frameRows(tiles?.features ?? []), [tiles]);
  const framePreview = useMemo(
    () => preview(rowsInFrame, DENSITY.tablePreview),
    [rowsInFrame],
  );

  /** Что из сужения карты переезжает в реестр контуров, а что — нет (DC-06). */
  const companion = useMemo(
    () =>
      contourListFilters({
        massifId: massifFilter,
        thematic,
        actorId: actorFocus,
        bbox,
      }),
    [massifFilter, thematic, actorFocus, bbox],
  );

  const massifOptions = useMemo<ReadonlyArray<ChoiceOption>>(
    () => [
      { value: "", label: t(m.levelWhole), hint: t(m.levelWholeHint) },
      ...palette.map((entry) => ({
        value: String(entry.id),
        label: entry.name,
        hint: t(m.inFrame, { count: massifsInView.byMassif.get(entry.id) ?? 0 }),
      })),
    ],
    [palette, massifsInView, t],
  );

  /**
   * Что открыто в превью. Нажатие по контуру больше НЕ уносит с карты: человек
   * терял кадр, масштаб и разобранные фильтры ради четырёх строк, которые
   * карта уже знает. Полный уровень остаётся ссылкой в панели.
   */
  const [picked, setPicked] = useState<ContourFeatureProps | null>(null);
  /** Строка таблицы под курсором или в фокусе — она же подсветка на полотне. */
  const [hover, setHover] = useState<number | null>(null);
  const handlePick = useCallback((props: ContourFeatureProps) => {
    setPicked(props);
  }, []);

  const columns: ReadonlyArray<Column<ContourRow>> = useMemo(
    () => [
      {
        key: "no",
        header: t(m.colNo),
        width: "minmax(90px, 0.8fr)",
        render: (row) => (
          <Link
            className={styles.rowLink}
            to={{
              pathname: `/contours/${row.id}`,
              search: context.linkSearch(),
            }}
            title={t(m.colOpen, { no: row.contour_no })}
          >
            {row.contour_no}
          </Link>
        ),
      },
      {
        key: "massif",
        header: t(m.colMassif),
        width: "minmax(160px, 1.6fr)",
        render: (row) => row.massif.canonical_name,
      },
      {
        key: "user",
        header: t(m.colUser),
        width: "minmax(200px, 2fr)",
        render: (row) =>
          row.land_users_count === 0 ? (
            <Badge tone="attention">{t(m.colNotSet)}</Badge>
          ) : row.land_user ? (
            (row.land_user.name ?? t(m.colSubject, { id: row.land_user.id }))
          ) : (
            <Badge>{countOf(row.land_users_count, WORDS.farm)}</Badge>
          ),
      },
      {
        key: "total",
        header: t(m.colTotal),
        width: "minmax(110px, 0.9fr)",
        align: "right",
        render: (row) => <Area microha={row.total_microha} unit={null} />,
      },
    ],
    [context, t],
  );

  if (reference.state === "loading" || coverage.state === "loading") {
    return <DataStatePanel state="loading" title={t(m.loadingLayers)} />;
  }

  // Пометка о происхождении приходит вместе с покрытием. Без неё карта не
  // показывается вовсе: производный полигон без объявления, откуда он и для
  // чего принят, визуально неотличим от официального.
  if (!coverage.data) {
    return (
      <DataStatePanel
        state="blocked"
        title={t(m.blocked)}
        action={
          <>
            <Button onClick={coverage.reload}>{t(COMMON.retry)}</Button>
            <Link to={{ pathname: "/contours", search: context.linkSearch() }}>
              <Button>{t(m.toRegistry)}</Button>
            </Link>
          </>
        }
      >
        {coverage.error?.message}
      </DataStatePanel>
    );
  }

  const cover = coverage.data;
  const withoutGeometryAreaFormatted = formatArea(cover.without_geometry_microha, {
    rounded: true,
  });
  const withoutGeometryArea = withoutGeometryAreaFormatted.missing
    ? withoutGeometryAreaFormatted.text
    : t(m.rowHa, { value: withoutGeometryAreaFormatted.text });
  const truncation = tiles ? truncationNotice(tiles) : null;
  const note = tiles?.note ?? cover.note;
  const partOf = cover.by_relation.find((row) => row.relation === "part_of");
  const tierRows = [...cover.by_tier].sort(
    (left, right) => tierRank(left.tier) - tierRank(right.tier),
  );
  // Отношения, при которых одна геометрия принадлежит нескольким контурам.
  // Берутся по реестру, а не по именам отношений в этом файле: список здесь
  // устарел бы ровно так же, как устарели три слоя обводки на карте.
  const sharedRelations = cover.by_relation.filter((row) =>
    isSharedRelation(row.relation),
  );
  const sharedFeatures = sharedRelations.reduce(
    (sum, row) => sum + row.features,
    0,
  );
  const sharedContours = sharedRelations.reduce(
    (sum, row) => sum + row.contours,
    0,
  );
  const sharedTierNames = TIER_REGISTRY.filter((entry) => entry.multiContour)
    .map((entry) => entry.label)
    .join(", ");
  return (
    <div className={features.stack}>
      <PageHeader
        title={t(m.title)}
        breadcrumb={
          <LevelTrail
            steps={[districtStep(context.linkSearch())]}
            screen={t(m.title)}
          />
        }
        purpose={t(m.purposeFill, { demo: demoLayers ? "yes" : "no" })}
        actions={
          <Link
            to={{ pathname: levelPath("contour"), search: context.linkSearch() }}
          >
            {/* Весь реестр, без сужения карты: сужённый список — отдельная
                ссылка в панели, и путать их нельзя. */}
            <Button>{t(m.allRegistry)}</Button>
          </Link>
        }
      />

      {/* Область объявляется РАНЬШЕ карты: полотно с половиной контуров и
          полотно района выглядят одинаково, и без этой плашки «сужено» не
          отличить от «геометрии нет». Источник — сам ответ карты, а при его
          отсутствии список контуров без геометрии. */}
      <ScopeNotice scope={tiles?.context.scope ?? withoutGeometry.scope} />

      {massifFilter ? (
        <InlineAlert title={t(m.massifAlertTitle)}>
          {t(m.massifAlertBody, {
            name: massifName(massifFilter) ?? `#${massifFilter}`,
            b: (chunks) => <strong>{chunks}</strong>,
          })}{" "}
          <Link
            to={{ pathname: `/massifs/${massifFilter}`, search: context.linkSearch() }}
          >
            {t(m.massifLevel)}
          </Link>{" "}
          ·{" "}
          <button
            type="button"
            className={styles.filterReset}
            onClick={() => patch(MASSIF_PARAM, null)}
          >
            {t(m.resetFilter)}
          </button>
        </InlineAlert>
      ) : null}

      {truncation ? (
        <InlineAlert title={t(m.truncTitle)}>
          {truncation.text}{" "}
          {t(m.truncHidden, {
            hidden: truncation.hidden,
            matching: truncation.matching,
            b: (chunks) => <strong>{chunks}</strong>,
          })}
        </InlineAlert>
      ) : null}

      {/* Разбор по КОДУ отказа, а не одно «не загрузилось»: при закрытом
          чтении сюда приходит 401, и «Повторить» на него бесполезно. Оговорка
          про предыдущую выдачу остаётся отдельным узлом — она про карту, а не
          про причину отказа, и текст причины всегда серверный. */}
      {tilesError ? (
        <RequestErrorPanel
          error={tilesError}
          title={t(m.tilesErrorTitle)}
          action={
            <Button onClick={() => setRetry((value) => value + 1)}>
              {t(COMMON.retry)}
            </Button>
          }
        />
      ) : null}
      {tilesError && tiles ? (
        <p className={styles.batchNote}>
          {t(m.tilesErrorStale)}
        </p>
      ) : null}

      <div className={styles.workspace}>
        <div className={styles.frame} data-guide="map-canvas">
          <MapCanvas
            district={district}
            mahallas={mahallas}
            contours={contours}
            fitBbox={fitBbox}
            palette={palette}
            cropPalette={cropFillPalette}
            thematic={thematic}
            massifName={massifName}
            onViewChange={setView}
            onPick={handlePick}
            highlightId={hover}
            focusIds={focusIds}
          />



          <div className={styles.statusPlate}>
            {tilesLoading ? t(m.statusLoading) : null}
            {!tilesLoading && tiles
              ? t(m.statusShown, {
                  returned: tiles.returned,
                  matching: tiles.matching,
                })
              : null}
            {!tilesLoading && !tiles ? t(m.statusIdle) : null}
          </div>

          <div className={styles.plateStack}>
            {/* Пока раскраска синтетическая, пометка видна всё это время и
                поверх полотна, а не в боковой карточке: цвет и его природа
                должны попадать в глаз одновременно. */}
            {demoLayers ? (
              <div className={styles.demoPlate}>
                <Badge tone="demo">{t(m.demoBadge)}</Badge>
                <span>{t(m.demoPlate)}</span>
              </div>
            ) : null}
            {/* Пометка постоянная и текстом сервера: переписывать её на свой
                лад значит подменять объявление источника. */}
            <div className={styles.derivedPlate}>{note}</div>
          </div>
        </div>

        <div className={features.stack} data-guide="map-controls">
          {/* --- Вид уровня ------------------------------------------------
              Карта — не отдельный раздел, а ВИД на уровень: район целиком либо
              один массив, при фокусе — земля одного хозяйства. Поэтому выбор
              уровня стоит первым в панели, а не приезжает только ссылкой с
              чужого экрана. */}
          <Card title={t(m.levelCard)}>
            <Select
              label={t(m.levelSelect)}
              explain={t(m.levelExplain)}
              options={massifOptions}
              value={massifFilter === null ? "" : String(massifFilter)}
              onChange={(value) => patch(MASSIF_PARAM, value)}
              size="sm"
              isDisabled={!massifs.data}
              description={
                massifs.data
                  ? undefined
                  : t(m.levelNoRef)
              }
            />

            {actorFocus === null ? null : (
              <ActorFocusPlate
                actor={actor}
                actorId={actorFocus}
                contoursOnMap={focusIds.length}
                linkSearch={context.linkSearch}
                onClear={() => patch(ACTOR_PARAM, null)}
              />
            )}

            {/* DC-06: тот же словарь фильтров, что у реестра. Переход обещает
                «то же списком» — значит обязан сказать, что списком НЕ
                переезжает. */}
            <div className={styles.crossLinks}>
              <Link
                to={{
                  pathname: levelPath("contour"),
                  search: context.linkSearch(companion.carried),
                }}
              >
                {t(m.sameList)}
              </Link>
              {massifFilter ? (
                <Link
                  to={{
                    pathname: levelPath("massif", massifFilter),
                    search: context.linkSearch(),
                  }}
                >
                  {t(m.massifLevel)}
                </Link>
              ) : null}
            </div>
            {companion.dropped.length > 0 ? (
              <p className={styles.batchNote}>
                {t(m.carryNote, {
                  items: companion.dropped
                    .map((item) => t(m.carryItem, { label: item.label, why: item.why }))
                    .join("; "),
                })}
              </p>
            ) : null}
          </Card>

          {/* --- Тематика --------------------------------------------------
              Чекбокс «демо-слои» превратился в выбор тематики: раскраска — это
              один слой с несколькими темами, а не набор независимых галочек.
              Недоступные темы остаются в списке названными: у карантинного
              статуса и состояния работы на карте нет источника, и молчание об
              этом читается как «такого не бывает». */}
          <Card title={t(m.themeCard)}>
            <Select
              label={t(m.themeSelect)}
              explain={t(m.themeExplain)}
              options={THEMATIC_OPTIONS.map((option) => ({
                value: option.value,
                label: option.label,
                hint: option.hint,
                isDisabled: !option.available,
              }))}
              value={thematic}
              onChange={(value) => patch(THEMATIC_PARAM, value === "massif" ? null : value)}
              size="sm"
              description={t(m.themeDesc)}
            />

            {thematic === "massif" ? (
              <>
                {massifs.error && !massifs.data ? (
                  <>
                    <RequestErrorPanel
                      error={massifs.error}
                      title={t(m.massifsFailed)}
                    />
                    <p className={styles.batchNote}>
                      {t(m.massifsFailedNote)}
                    </p>
                  </>
                ) : massifLegend.shown.length === 0 ? (
                  <p className={styles.batchNote}>
                    {t(m.frameEmptyLegend)}
                  </p>
                ) : (
                  <>
                    {/* Легенда видна ВСЕГДА: она называет цвета кадра, а не
                        весь справочник района. */}
                    <div className={styles.legend}>
                      {massifLegend.shown.map((entry) => (
                        <div className={styles.legendRow} key={entry.id}>
                          <span
                            className={styles.swatch}
                            // Единственный вычисляемый цвет на экране: палитра
                            // массивов, детерминированная по имени (mapModel).
                            style={{ background: entry.color }}
                            aria-hidden
                          />
                          <span className={styles.legendName}>{entry.name}</span>
                          <span className={styles.legendCount}>
                            {t(m.inFrame, { count: entry.inView })}
                          </span>
                        </div>
                      ))}
                    </div>
                    {massifLegend.hidden > 0 ? (
                      <Disclosure
                        title={t(m.moreMassifs, { count: massifLegend.hidden })}
                        tone="inline"
                        more={t(m.showColors)}
                      >
                        <div className={styles.legend}>
                          {palette
                            .filter(
                              (entry) =>
                                (massifsInView.byMassif.get(entry.id) ?? 0) > 0 &&
                                !massifLegend.shown.some(
                                  (shown) => shown.id === entry.id,
                                ),
                            )
                            .map((entry) => (
                              <div className={styles.legendRow} key={entry.id}>
                                <span
                                  className={styles.swatch}
                                  style={{ background: entry.color }}
                                  aria-hidden
                                />
                                <span className={styles.legendName}>
                                  {entry.name}
                                </span>
                                <span className={styles.legendCount}>
                                  {t(m.inFrame, {
                                    count: massifsInView.byMassif.get(entry.id) ?? 0,
                                  })}
                                </span>
                              </div>
                            ))}
                        </div>
                      </Disclosure>
                    ) : null}
                    <p className={styles.batchNote}>
                      {t(m.registryMassifs, { count: palette.length })}
                    </p>
                  </>
                )}
              </>
            ) : null}

            {demoLayers ? (
              <>
                <InlineAlert title={t(m.demoInfoTitle)}>
                  {t(m.demoInfoBody)}
                </InlineAlert>

                <div className={styles.legend}>
                  {cropFill.shown
                    .filter((entry) => entry.code !== OTHER_CROPS)
                    .map((entry) => {
                      const inView = cropsInView.byCrop.get(entry.code) ?? 0;
                      return (
                        <div className={styles.legendRow} key={entry.code}>
                          <span
                            className={styles.swatch}
                            style={{ background: entry.color }}
                            aria-hidden
                          />
                          <span className={styles.legendName}>{entry.name}</span>
                          <span className={styles.legendCount}>
                            {t(m.inFrame, { count: inView })}
                          </span>
                        </div>
                      );
                    })}
                  {cropOther ? (
                    <div className={styles.legendRow}>
                      <span
                        className={styles.swatch}
                        style={{ background: cropTokenColor("--data-crop-other") }}
                        aria-hidden
                      />
                      <span className={styles.legendName}>
                        {cropOther.folded.length > 0
                          ? t(m.otherCropsFolded, { count: cropOther.folded.length })
                          : t(m.otherCrops)}
                      </span>
                      <span className={styles.legendCount}>
                        {t(m.inFrame, { count: cropOther.count })}
                      </span>
                    </div>
                  ) : null}
                  <div className={styles.legendRow}>
                    <span className={styles.contextSampleSoft} aria-hidden />
                    <span className={styles.legendName}>
                      {t(m.nothingSown)}
                    </span>
                    <span className={styles.legendCount}>
                      {t(m.inFrame, { count: cropsInView.unpainted })}
                    </span>
                  </div>
                </div>

                {placementCoverage.data ? (
                  <Disclosure
                    title={t(m.covTableTitle)}
                    tone="inline"
                    more={t(m.covTableMore)}
                  >
                    <table className={features.plainTable}>
                      <caption>{t(m.covCaption)}</caption>
                      <thead>
                        <tr>
                          <th scope="col">{t(m.covCrop)}</th>
                          <th scope="col">{t(m.covDemoHa)}</th>
                          <th scope="col">{t(m.covContours)}</th>
                          <th scope="col">{t(m.covOnMap)}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {placementCoverage.data.by_crop.map((row) => (
                          <tr key={row.crop_code}>
                            <td>{cropLabel(row.crop_code).label}</td>
                            <td className={features.num}>
                              <Area microha={row.demo_microha} unit={null} />
                            </td>
                            <td className={features.num}>
                              <Count value={row.contours} />
                            </td>
                            <td className={features.num}>
                              <Count value={row.contours_with_geometry} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <th scope="row">{t(m.covTotal)}</th>
                          <td className={features.num}>
                            <Area
                              microha={placementCoverage.data.demo_microha}
                              unit={null}
                            />
                          </td>
                          <td className={features.num}>
                            <Count value={placementCoverage.data.demo_contours} />
                          </td>
                          <td className={features.num}>
                            <Count
                              value={
                                placementCoverage.data.demo_contours_with_geometry
                              }
                            />
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">{t(m.covSource)}</th>
                          <td className={features.num}>
                            <Area
                              microha={
                                placementCoverage.data.source_total_sown_microha
                              }
                              unit={null}
                            />
                          </td>
                          <td className={features.num} colSpan={2}>
                            {t(m.covPlacements, {
                              count: formatCount(
                                placementCoverage.data.placements_total,
                              ).text,
                            })}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </Disclosure>
                ) : null}

                {placementCoverage.data?.batches.map((batch) => (
                  <p className={styles.batchNote} key={batch.id}>
                    {t(m.batchNote, {
                      id: batch.id,
                      generator: batch.generator_version,
                      norms: batch.norms_version,
                      seed: batch.seed,
                    })}
                    {batch.unallocatable_measures
                      ? ` ${t(m.batchUnallocated, {
                          count: batch.unallocatable_measures,
                          area: (
                            (batch.unallocatable_microha ?? 0) / 1_000_000
                          ).toFixed(1),
                        })}`
                      : ""}
                  </p>
                ))}

                {placementCoverage.data &&
                !placementCoverage.data.demo_available ? (
                  <DataStatePanel state="empty" title={t(m.demoMissingTitle)}>
                    {t(m.demoMissingBody)}
                  </DataStatePanel>
                ) : null}
              </>
            ) : null}
          </Card>

          <Card title={t(m.tierCard)}>
            <div className={styles.legend}>
              {tierRows.map((row) => {
                // Образец и подпись берутся из реестра по коду яруса. Раньше
                // здесь стоял тернарник «auto → scored → ИНАЧЕ group»: любой
                // ярус, кроме первых двух, получал образец группового яруса и
                // подпись про «часть контура» — то есть ровно обратное тому,
                // что означают merge и mn_group.
                const entry = tierEntry(row.tier);
                return (
                  <div className={styles.legendRow} key={row.tier}>
                    <span
                      className={`${styles.tierSample} ${
                        styles[entry?.swatch ?? UNKNOWN_TIER.swatch]
                      }`}
                      aria-hidden
                    />
                    <span className={styles.legendName}>
                      {entry
                        ? t(m.tierName, {
                            tier: tierLabel(row.tier).label,
                            relation: entry.relationLabel,
                          })
                        : tierLabel(row.tier).label}
                    </span>
                    <span className={styles.legendCount}>
                      {t(m.tierCounts, {
                        contours: countOf(row.contours, WORDS.contour),
                        features: countOf(row.features, m.feature),
                      })}
                    </span>
                  </div>
                );
              })}
              <div className={features.stackTight}>
                <span>{t(m.tierNote)}</span>
                {sharedTierNames ? (
                  <span>{t(m.tierShared, { names: sharedTierNames })}</span>
                ) : null}
              </div>
            </div>
          </Card>

          <Card title={t(m.ctxCard)}>
            <div className={styles.legend}>
              <div className={styles.legendRow}>
                <span className={styles.contextSample} aria-hidden />
                <span className={styles.legendName}>{t(m.ctxDistrict)}</span>
              </div>
              <div className={styles.legendRow}>
                <span className={styles.contextSampleSoft} aria-hidden />
                <span className={styles.legendName}>{t(m.ctxMahalla)}</span>
              </div>
              <span>{t(m.ctxNote)}</span>
            </div>
          </Card>
        </div>
      </div>

      {/* --- Таблица-компаньон ------------------------------------------
          Клавиатурный эквивалент полотна: то же, что нарисовано, строками.
          Наведение и фокус строки подсвечивают контур на карте, нажатие
          открывает то же превью, что и нажатие по полигону. Без неё карта
          остаётся недостижимой ни клавиатурой, ни озвучивателем. */}
      <div data-guide="map-frame-list"><Card
        title={t(m.frameCard, { count: rowsInFrame.length })}
        large
      >
        {rowsInFrame.length === 0 ? (
          <DataStatePanel state="empty" title={t(m.frameEmptyTitle)}>
            {t(m.frameEmptyBody)}
          </DataStatePanel>
        ) : (
          <>
            <p className={styles.batchNote}>
              {t(m.frameIntro)}
              {truncation ? ` ${t(m.frameTrunc)}` : ""}
            </p>
            {/* Шапка помечена `aria-hidden`: у строки-кнопки есть собственное
                доступное имя со всеми четырьмя величинами, и вторая
                озвученная шапка добавила бы к нему шум. */}
            <div className={`${styles.frameRow} ${styles.frameHead}`} aria-hidden>
              <span>{t(m.frameHeadContour)}</span>
              <span>{t(m.frameHeadMassif)}</span>
              <span>{t(m.frameHeadUser)}</span>
              <span className={styles.frameArea}>{t(m.frameHeadArea)}</span>
            </div>
            <ul className={styles.frameList}>
              {framePreview.shown.map((row) => (
                <FrameRow
                  key={row.contour_id}
                  row={row}
                  massifName={massifName(row.massif_id)}
                  onHover={setHover}
                  onOpen={setPicked}
                />
              ))}
            </ul>
            {framePreview.hidden > 0 ? (
              <Disclosure
                title={t(m.moreContours, { count: framePreview.hidden })}
                more={t(m.showRows)}
              >
                <ul className={styles.frameList}>
                  {rowsInFrame.slice(DENSITY.tablePreview).map((row) => (
                    <FrameRow
                      key={row.contour_id}
                      row={row}
                      massifName={massifName(row.massif_id)}
                      onHover={setHover}
                      onOpen={setPicked}
                    />
                  ))}
                </ul>
              </Disclosure>
            ) : null}
          </>
        )}
      </Card></div>

      <span className="visually-hidden">{t(m.a11yDouble)}</span>

      <div className={`${layout.grid} ${layout.grid3}`} data-guide="map-coverage">
        <MetricCard
          label={t(m.kpiWith)}
          value={<Count value={cover.contours_with_geometry} />}
          bottom={t(m.kpiWithBottom, {
            total: formatCount(cover.contours_total).text,
            share: formatShare(
              share(cover.contours_with_geometry, cover.contours_total),
            ),
          })}
        />
        <MetricCard
          label={t(m.kpiHa)}
          value={<Area microha={cover.with_geometry_microha} rounded />}
          bottom={t(m.kpiHaBottom, {
            share: formatShare(
              share(cover.with_geometry_microha, cover.total_microha),
            ),
          })}
        />
        <MetricCard
          label={t(m.kpiWithout)}
          value={<Count value={cover.contours_without_geometry} />}
          bottom={t(m.kpiWithoutBottom, {
            area: withoutGeometryArea,
          })}
        />
      </div>

      <div data-guide="map-coverage-table"><Card title={t(m.covCard)} large>
        <div className={features.scrollX}>
          <table className={features.plainTable}>
            <caption>{t(m.covCap)}</caption>
            <thead>
              <tr>
                <th scope="col">{t(m.covWhat)}</th>
                <th scope="col" className={features.num}>
                  {t(m.covColContours)}
                </th>
                <th scope="col" className={features.num}>
                  {t(m.covColHa)}
                </th>
                <th scope="col" className={features.num}>
                  {t(m.covColShare)}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">{t(m.covRowWith)}</th>
                <td className={features.num}>
                  <Count value={cover.contours_with_geometry} />
                </td>
                <td className={features.num}>
                  <Area microha={cover.with_geometry_microha} unit={null} />
                </td>
                <td className={features.num}>
                  {formatShare(
                    share(cover.with_geometry_microha, cover.total_microha),
                  )}
                </td>
              </tr>
              <tr>
                <th scope="row">{t(m.covRowWithout)}</th>
                <td className={features.num}>
                  <Count value={cover.contours_without_geometry} />
                </td>
                <td className={features.num}>
                  <Area microha={cover.without_geometry_microha} unit={null} />
                </td>
                <td className={features.num}>
                  {formatShare(
                    share(cover.without_geometry_microha, cover.total_microha),
                  )}
                </td>
              </tr>
              <tr>
                <th scope="row">{t(m.covRowAll)}</th>
                <td className={features.num}>
                  <Count value={cover.contours_total} />
                </td>
                <td className={features.num}>
                  <Area microha={cover.total_microha} unit={null} />
                </td>
                <td className={features.num}>
                  {formatShare(share(cover.total_microha, cover.total_microha))}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <table className={features.plainTable}>
          <caption>{t(m.partsCaption)}</caption>
          <tbody>
            <tr>
              <th scope="row">
                {t(m.partsRowPart, { relation: relationLabel("part_of").label })}
              </th>
              <td className={features.num}>
                <Count value={partOf?.features ?? null} />
              </td>
              <td>
                {t(m.partsPartNote, {
                  contours: countOf(partOf?.contours ?? 0, WORDS.contour),
                })}
              </td>
            </tr>
            {sharedFeatures > 0 ? (
              <tr>
                <th scope="row">{t(m.partsRowShared)}</th>
                <td className={features.num}>
                  <Count value={sharedFeatures} />
                </td>
                <td>
                  {t(m.partsSharedNote, {
                    contours: countOf(sharedContours, WORDS.contour),
                  })}
                </td>
              </tr>
            ) : null}
            <tr>
              <th scope="row">{t(m.partsRowCanon)}</th>
              <td className={features.num}>
                <Count value={cover.canon_contour_parts} />
              </td>
              <td>{t(m.partsCanonNote)}</td>
            </tr>
            <tr>
              <th scope="row">{t(m.partsRowNoArea)}</th>
              <td className={features.num}>
                <Count value={cover.contours_without_area_rows} />
              </td>
              <td>{t(m.partsNoAreaNote)}</td>
            </tr>
          </tbody>
        </table>
        <p className={layout.pagePurpose}>{cover.note}</p>
      </Card></div>

      <div data-guide="map-no-geometry"><Card title={t(m.noGeoCard)} large>
        {withoutGeometry.error && withoutGeometry.total === null ? (
          <RequestErrorPanel
            error={withoutGeometry.error}
            title={t(m.noGeoError)}
            action={
              <Button onClick={withoutGeometry.reload}>{t(COMMON.retry)}</Button>
            }
          />
        ) : (
          <VirtualTable
            columns={columns}
            total={withoutGeometry.total}
            rows={withoutGeometry.rows}
            onRangeChange={withoutGeometry.ensureRange}
            rowKey={(row) => row.id}
            caption={t(m.noGeoCard)}
            emptyLabel={t(m.noGeoEmpty)}
            meta={
              <>
                <span>
                  {withoutGeometry.total === null
                    ? t(m.noGeoFoundLoading, {
                        b: (chunks) => <strong>{chunks}</strong>,
                      })
                    : t(m.noGeoFound, {
                        total: withoutGeometry.total,
                        b: (chunks) => <strong>{chunks}</strong>,
                      })}
                  {withoutGeometry.loading ? ` · ${t(m.noGeoUpdating)}` : ""}
                </span>
                <span>{t(m.noGeoNote)}</span>
              </>
            }
            footNote={
              withoutGeometry.total !== null
                ? t(m.noGeoLoadedOf, {
                    loaded: withoutGeometry.loaded,
                    total: withoutGeometry.total,
                  })
                : t(m.noGeoLoaded, { loaded: withoutGeometry.loaded })
            }
          />
        )}
      </Card></div>

      {/* Превью нажатого контура. Панель модальная и закрывается по Esc;
          полный уровень — обычной ссылкой внизу, её можно скопировать. */}
      <DetailDrawer
        open={picked !== null}
        title={picked ? t(m.previewTitle, { no: picked.contour_no }) : ""}
        onClose={() => setPicked(null)}
        footer={
          picked ? (
            <MapPreviewAction
              contourId={picked.contour_id}
              linkSearch={context.linkSearch}
            />
          ) : null
        }
      >
        {picked ? (
          <MapPreviewCard
            props={picked}
            massifName={massifName(picked.massif_id)}
            links={
              <MapPreviewLevelLinks
                props={picked}
                massifName={massifName(picked.massif_id)}
                linkSearch={context.linkSearch}
              />
            }
          />
        ) : null}
      </DetailDrawer>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Таблица-компаньон                                                   */
/* ------------------------------------------------------------------ */

/**
 * Строка кадра — кнопка, а не ссылка.
 *
 * Нажатие открывает превью на месте (то же, что нажатие по полигону), и увести
 * со страницы оно не должно: адрес контура живёт в самой панели. Наведение и
 * фокус — одно и то же событие для карты: человек, идущий по списку Tab'ом,
 * обязан видеть на полотне ровно то, что видит человек с мышью.
 */
function FrameRow({
  row,
  massifName,
  onHover,
  onOpen,
}: {
  row: ContourFeatureProps;
  massifName: string | null;
  onHover: (id: number | null) => void;
  onOpen: (props: ContourFeatureProps) => void;
}) {
  const t = useT();
  const area = formatArea(row.total_microha);
  const landUser =
    row.land_users_count === 0
      ? t(m.rowNoUser)
      : (row.land_user ?? countOf(row.land_users_count, WORDS.farm));
  const massifText = massifName ?? t(m.rowMassifUnnamed, { id: row.massif_id });

  return (
    <li>
      <button
        type="button"
        className={styles.frameRow}
        // Имя строки собрано целиком: без него озвучиватель читает четыре
        // величины подряд, не говоря, какая из них что.
        aria-label={t(m.rowLabel, {
          no: row.contour_no,
          massif: massifText,
          user: landUser,
          area: area.missing ? t(m.rowAreaMissing) : t(m.rowHa, { value: area.text }),
        })}
        onMouseEnter={() => onHover(row.contour_id)}
        onMouseLeave={() => onHover(null)}
        onFocus={() => onHover(row.contour_id)}
        onBlur={() => onHover(null)}
        onClick={() => onOpen(row)}
      >
        <span className={styles.frameNo}>{row.contour_no}</span>
        <span className={styles.frameCell}>
          {massifText}
        </span>
        <span className={styles.frameCell}>
          {row.land_users_count === 0 ? (
            <Badge tone="attention">{t(m.rowNoUser)}</Badge>
          ) : (
            landUser
          )}
        </span>
        <span className={styles.frameArea}>
          <Area microha={row.total_microha} unit={null} />
        </span>
      </button>
    </li>
  );
}

/* ------------------------------------------------------------------ */
/* Фокус уровня хозяйства                                              */
/* ------------------------------------------------------------------ */

/**
 * Что показывает фокус на хозяйстве — и чего он НЕ делает.
 *
 * Это подсветка, а не выборка: карта продолжает показывать весь кадр, соседние
 * контуры приглушены, но видны. Разница существенная — «карта хозяйства» без
 * соседей читалась бы как «вокруг ничего не зарегистрировано», а это неправда.
 *
 * Второе, что плашка обязана сказать вслух: контуры хозяйства БЕЗ принятой
 * геометрии на карте не появятся никак. Их число здесь названо, а не оставлено
 * читателю в виде разницы двух чисел на разных экранах.
 */
function ActorFocusPlate({
  actor,
  actorId,
  contoursOnMap,
  linkSearch,
  onClear,
}: {
  actor: { data: ActorDetail | null; error: ApiError | null; state: string };
  actorId: number;
  contoursOnMap: number;
  linkSearch: (extra?: Record<string, string | number>) => string;
  onClear: () => void;
}) {
  const t = useT();
  const name = actor.data?.name ?? t(m.focusName, { id: actorId });

  if (actor.state === "loading") {
    return <p className={styles.batchNote}>{t(m.focusLoading)}</p>;
  }

  if (!actor.data) {
    return (
      <InlineAlert title={t(m.focusErrorTitle)}>
        {t(m.focusErrorBody)}{" "}
        {actor.error?.message}{" "}
        <button type="button" className={styles.filterReset} onClick={onClear}>
          {t(m.focusReset)}
        </button>
      </InlineAlert>
    );
  }

  const withoutGeometry = Math.max(0, actor.data.land_uses.length - contoursOnMap);

  return (
    <InlineAlert title={t(m.focusTitle, { name })}>
      {t(m.focusBody, {
        uses: actor.data.land_uses.length,
        contours: contoursOnMap,
        b: (chunks) => <strong>{chunks}</strong>,
      })}
      {withoutGeometry > 0 ? ` ${t(m.focusNoGeo)}` : ""}{" "}
      <Link to={{ pathname: levelPath("actor", actorId), search: linkSearch() }}>
        {t(m.focusLevel)}
      </Link>{" "}
      ·{" "}
      <button type="button" className={styles.filterReset} onClick={onClear}>
        {t(m.focusReset)}
      </button>
    </InlineAlert>
  );
}
